import { useEffect, useMemo, useRef, useState } from "react";
import { stringOptionsWithIcon } from "../../../components/forms";
import { anomalyGuideService } from "../../../services/anomalyGuideService";

type ClassificationSource = {
  category?: string | null;
  subcategory?: string | null;
};

type TaxonomyRow = {
  category: string;
  subCategory: string;
  classificationGuidance: string;
};

/** Guide text for a category and subcategory pair. Null when that pair is not in the guide. */
export function anomalyGuideDescription(
  taxonomy: TaxonomyRow[],
  category?: string | null,
  subcategory?: string | null,
): string | null {
  const categoryText = category?.trim() || "";
  const subcategoryText = subcategory?.trim() || "";
  if (!categoryText || !subcategoryText) return null;
  const match = taxonomy.find(
    (row) => row.category === categoryText && row.subCategory === subcategoryText,
  );
  return match ? match.classificationGuidance : null;
}

export function useAnomalyGuideClassification(source: ClassificationSource, resetKey: string) {
  const sourceRef = useRef(source);
  sourceRef.current = source;

  const [category, setCategory] = useState<string | null>(source.category?.trim() || null);
  const [subcategory, setSubcategory] = useState<string | null>(source.subcategory?.trim() || null);
  const [taxonomy, setTaxonomy] = useState<TaxonomyRow[]>([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);
  const [taxonomyReady, setTaxonomyReady] = useState(false);

  useEffect(() => {
    const next = sourceRef.current;
    setCategory(next.category?.trim() || null);
    setSubcategory(next.subcategory?.trim() || null);
  }, [resetKey]);

  useEffect(() => {
    let active = true;
    setTaxonomyLoading(true);
    setTaxonomyReady(false);
    void anomalyGuideService
      .listTaxonomy()
      .then((rows) => {
        if (active) {
          setTaxonomy(rows);
          setTaxonomyReady(true);
        }
      })
      .catch((error) => {
        console.error("Failed to load anomaly guide categories:", error);
        if (active) {
          setTaxonomy([]);
          setTaxonomyReady(false);
        }
      })
      .finally(() => {
        if (active) {
          setTaxonomyLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const categoryOptions = useMemo(() => {
    const values = new Set(taxonomy.map((row) => row.category).filter(Boolean));
    if (category) {
      values.add(category);
    }
    return stringOptionsWithIcon(
      Array.from(values).sort((a, b) => a.localeCompare(b)),
      "category",
    );
  }, [taxonomy, category]);

  const subcategoryOptions = useMemo(() => {
    if (!category) {
      return [];
    }
    const values = new Set(
      taxonomy
        .filter((row) => row.category === category && row.subCategory)
        .map((row) => row.subCategory),
    );
    if (subcategory && (taxonomyLoading || !taxonomyReady || values.has(subcategory))) {
      values.add(subcategory);
    }
    return stringOptionsWithIcon(
      Array.from(values).sort((a, b) => a.localeCompare(b)),
      "account_tree",
    );
  }, [taxonomy, category, subcategory, taxonomyLoading, taxonomyReady]);

  useEffect(() => {
    if (!taxonomyReady || !category) {
      return;
    }
    const allowed = new Set(
      taxonomy.filter((row) => row.category === category).map((row) => row.subCategory),
    );
    setSubcategory((current) => (current && allowed.has(current) ? current : null));
  }, [taxonomyReady, taxonomy, category, subcategory]);

  const handleCategoryChange = (next: string | null) => {
    setCategory(next);
    if (!next) {
      setSubcategory(null);
      return;
    }
    const allowed = new Set(
      taxonomy.filter((row) => row.category === next).map((row) => row.subCategory),
    );
    setSubcategory((current) => (current && allowed.has(current) ? current : null));
  };

  const classificationGuidance = useMemo(
    () => anomalyGuideDescription(taxonomy, category, subcategory),
    [taxonomy, category, subcategory],
  );

  return {
    category,
    subcategory,
    setCategory: handleCategoryChange,
    setSubcategory,
    categoryOptions,
    subcategoryOptions,
    classificationGuidance,
    taxonomyLoading,
    taxonomyReady,
  };
}
